import { Home, Search, Heart, ShoppingCart, Menu, Package, Settings, LogOut, List, HelpCircle, BookOpen, Store, MessageSquare, X, Sparkles, ChevronRight, User as UserIcon, LogIn } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useQuery } from "@tanstack/react-query";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

interface Category {
  id: string;
  name: string;
  slug: string;
}

const fetchCategories = async (): Promise<Category[]> => {
  const { data } = await supabase
    .from("categories")
    .select("id, name, slug")
    .order("name");
  return data || [];
};

const MobileBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { items: cartItems } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState("");

  const [isReady, setIsReady] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const cartItemCount = cartItems?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  const { data: isSeller = false } = useQuery({
    queryKey: ['seller-status', user?.id],
    queryFn: async () => {
      if (!user) return false;
      const { data } = await supabase
        .from("seller_profiles")
        .select("status")
        .eq("user_id", user.id)
        .single();
      return data?.status === "approved";
    },
    enabled: !!user && isReady,
    staleTime: 1000 * 60 * 10,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['categories-nav'],
    queryFn: fetchCategories,
    enabled: isReady,
    staleTime: 1000 * 60 * 10,
  });

  const displayCategories = useMemo(() => categories.slice(0, 6), [categories]);

  const hiddenRoutes = ["/checkout"];
  const shouldHide = hiddenRoutes.includes(location.pathname);

  const handleMobileSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (mobileSearch.trim()) {
      navigate(`/productos?buscar=${encodeURIComponent(mobileSearch.trim())}`);
      setMenuOpen(false);
      setMobileSearch("");
    }
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    setMenuOpen(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setMenuOpen(false);
    navigate("/");
  };

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  if (shouldHide) return null;

  const NavItem = ({ to, icon: Icon, label, badge }: { to: string; icon: any; label: string; badge?: number }) => {
    const active = isActive(to);
    return (
      <Link
        to={to}
        className="flex flex-col items-center justify-center gap-1 flex-1 py-1.5 touch-target select-none"
      >
        <div
          className={`relative flex items-center justify-center h-9 w-12 rounded-full transition-colors ${
            active ? "bg-primary/10" : ""
          }`}
        >
          <Icon
            className={`h-[22px] w-[22px] transition-colors ${
              active ? "text-primary" : "text-muted-foreground"
            }`}
            strokeWidth={active ? 2.4 : 2}
          />
          {badge !== undefined && badge > 0 && (
            <span className="absolute top-0 right-1 bg-primary text-primary-foreground text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center ring-2 ring-background">
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </div>
        <span
          className={`text-[10.5px] leading-none transition-colors ${
            active ? "text-primary font-semibold" : "text-muted-foreground font-medium"
          }`}
        >
          {label}
        </span>
      </Link>
    );
  };

  const Row = ({
    icon: Icon,
    label,
    onClick,
    accent,
  }: {
    icon: any;
    label: string;
    onClick: () => void;
    accent?: boolean;
  }) => (
    <button
      onClick={onClick}
      className="flex items-center gap-3 w-full px-3 py-3 text-left rounded-xl active:bg-muted/70 hover:bg-muted/50 transition-colors touch-target"
    >
      <span
        className={`flex items-center justify-center h-9 w-9 rounded-full ${
          accent ? "bg-primary/10 text-primary" : "bg-muted text-foreground/80"
        }`}
      >
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <span className={`flex-1 text-[15px] font-medium ${accent ? "text-primary" : ""}`}>
        {label}
      </span>
      <ChevronRight className="h-4 w-4 text-muted-foreground/60" />
    </button>
  );

  const SectionLabel = ({ children }: { children: React.ReactNode }) => (
    <h3 className="text-[11px] font-semibold text-muted-foreground/80 uppercase tracking-wider px-3 mb-1 mt-4 first:mt-0">
      {children}
    </h3>
  );

  const userInitial = (user?.email || "U").charAt(0).toUpperCase();

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-background/90 backdrop-blur-xl border-t border-border/60 z-50 pb-safe">
      <div className="flex items-center justify-around h-16 px-2 max-w-md mx-auto">
        <NavItem to="/" icon={Home} label="Inicio" />
        <NavItem to="/tiendas" icon={Store} label="Tiendas" />
        <NavItem to="/productos" icon={Search} label="Buscar" />
        <NavItem to="/carrito" icon={ShoppingCart} label="Carrito" badge={cartItemCount} />

        <Drawer open={menuOpen} onOpenChange={setMenuOpen}>
          <DrawerTrigger asChild>
            <button className="flex flex-col items-center justify-center gap-1 flex-1 py-1.5 touch-target select-none">
              <div
                className={`flex items-center justify-center h-9 w-12 rounded-full transition-colors ${
                  menuOpen ? "bg-primary/10" : ""
                }`}
              >
                <Menu
                  className={`h-[22px] w-[22px] ${menuOpen ? "text-primary" : "text-muted-foreground"}`}
                  strokeWidth={menuOpen ? 2.4 : 2}
                />
              </div>
              <span
                className={`text-[10.5px] leading-none ${
                  menuOpen ? "text-primary font-semibold" : "text-muted-foreground font-medium"
                }`}
              >
                Menú
              </span>
            </button>
          </DrawerTrigger>

          <DrawerContent className="max-h-[88vh] flex flex-col">
            <DrawerHeader className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-border/60">
              <DrawerTitle className="text-base font-semibold">Menú</DrawerTitle>
              <DrawerClose asChild>
                <button className="p-2 -mr-2 rounded-full hover:bg-muted active:bg-muted/80 transition-colors">
                  <X className="h-5 w-5 text-muted-foreground" />
                </button>
              </DrawerClose>
            </DrawerHeader>

            <ScrollArea className="flex-1 overflow-y-auto">
              <div className="px-3 py-3 space-y-1">
                {/* Account hero */}
                {user ? (
                  <button
                    onClick={() => handleNavClick("/configuracion")}
                    className="flex items-center gap-3 w-full p-3 rounded-2xl bg-muted/50 active:bg-muted transition-colors"
                  >
                    <span className="flex items-center justify-center h-11 w-11 rounded-full bg-primary text-primary-foreground text-base font-semibold shrink-0">
                      {userInitial}
                    </span>
                    <span className="flex-1 text-left min-w-0">
                      <span className="block text-[15px] font-semibold truncate">
                        {user.user_metadata?.full_name || "Mi cuenta"}
                      </span>
                      <span className="block text-xs text-muted-foreground truncate">
                        {user.email}
                      </span>
                    </span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground/60 shrink-0" />
                  </button>
                ) : (
                  <button
                    onClick={() => handleNavClick("/iniciar-sesion")}
                    className="flex items-center gap-3 w-full p-3 rounded-2xl bg-primary text-primary-foreground active:opacity-90 transition-opacity"
                  >
                    <span className="flex items-center justify-center h-11 w-11 rounded-full bg-primary-foreground/15 shrink-0">
                      <LogIn className="h-5 w-5" />
                    </span>
                    <span className="flex-1 text-left">
                      <span className="block text-[15px] font-semibold">Inicia sesión</span>
                      <span className="block text-xs opacity-90">Accede a tus compras y favoritos</span>
                    </span>
                  </button>
                )}

                {/* Search */}
                <form onSubmit={handleMobileSearch} className="pt-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <input
                      type="text"
                      value={mobileSearch}
                      onChange={(e) => setMobileSearch(e.target.value)}
                      placeholder="Buscar productos o tiendas"
                      className="w-full pl-10 pr-9 py-3 bg-muted/60 rounded-2xl border-0 focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm"
                    />
                    {mobileSearch && (
                      <button
                        type="button"
                        onClick={() => setMobileSearch("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </form>

                {/* Ver todos los productos */}
                <button
                  onClick={() => handleNavClick("/productos")}
                  className="flex items-center gap-3 w-full mt-3 p-3 rounded-2xl bg-primary/10 active:bg-primary/20 transition-colors"
                >
                  <span className="flex items-center justify-center h-10 w-10 rounded-full bg-primary text-primary-foreground shrink-0">
                    <Search className="h-[18px] w-[18px]" />
                  </span>
                  <span className="flex-1 text-left text-[15px] font-semibold text-primary">
                    Ver todos los productos
                  </span>
                  <ChevronRight className="h-4 w-4 text-primary/70 shrink-0" />
                </button>


                {/* Mi cuenta */}
                {user && (
                  <>
                    <SectionLabel>Mi cuenta</SectionLabel>
                    <div>
                      <Row icon={Package} label="Mis compras" onClick={() => handleNavClick("/mis-pedidos")} />
                      <Row icon={Heart} label="Favoritos" onClick={() => handleNavClick("/favoritos")} />
                      <Row icon={MessageSquare} label="Mensajes" onClick={() => handleNavClick("/mensajes")} />
                      <Row icon={List} label="Listas de regalos" onClick={() => handleNavClick("/listas-de-regalos")} />
                      {isSeller && (
                        <Row icon={Store} label="Gestor de tienda" onClick={() => handleNavClick("/shop-manager")} accent />
                      )}
                      <Row icon={Settings} label="Configuración" onClick={() => handleNavClick("/configuracion")} />
                    </div>
                  </>
                )}

                {/* Descubrir */}
                <SectionLabel>Descubrir</SectionLabel>
                <div>
                  <Row icon={Store} label="Tiendas" onClick={() => handleNavClick("/tiendas")} />
                  {!user && (
                    <>
                      <Row icon={Heart} label="Favoritos" onClick={() => handleNavClick("/favoritos")} />
                      <Row icon={List} label="Listas de regalos" onClick={() => handleNavClick("/listas-de-regalos")} />
                    </>
                  )}
                  <Row icon={BookOpen} label="Blog" onClick={() => handleNavClick("/blog")} />
                </div>

                {/* Vender */}
                {!isSeller && (
                  <>
                    <SectionLabel>Vender</SectionLabel>
                    <div>
                      <Row
                        icon={Sparkles}
                        label="Comenzar a vender"
                        onClick={() => handleNavClick("/registro-vendedor")}
                        accent
                      />
                      <Row icon={Store} label="Beneficios de vender" onClick={() => handleNavClick("/vender")} />
                    </div>
                  </>
                )}

                {/* Ayuda */}
                <SectionLabel>Ayuda</SectionLabel>
                <div className="pb-2">
                  <Row icon={HelpCircle} label="Soporte" onClick={() => handleNavClick("/soporte")} />
                </div>
              </div>
            </ScrollArea>

            {user && (
              <div className="flex-shrink-0 border-t border-border/60 bg-background/95 backdrop-blur-sm p-3 pb-safe">
                <Button
                  variant="ghost"
                  className="w-full h-11 text-destructive hover:text-destructive hover:bg-destructive/10 rounded-xl text-sm font-medium"
                  onClick={handleLogout}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Cerrar sesión
                </Button>
              </div>
            )}
          </DrawerContent>
        </Drawer>
      </div>
    </nav>
  );
};

export default MobileBottomNav;
